/**
 * Workspace Store — manajemen state workspace EKINKINIAN.
 *
 * Menggunakan Svelte 5 runes ($state) di module scope (.svelte.ts)
 * agar state reaktif global. Komponen mengakses via getter `workspace`
 * yang mempertahankan reaktivitas.
 *
 * Tanggung jawab:
 * - Memuat daftar workspace milik user dari SQLite cache (S5).
 * - Auto-create workspace default saat pertama login (FR-WS-01).
 * - Menyimpan & memulihkan workspace aktif via sync_meta.
 * - Operasi CRUD workspace (create, update, delete, switch).
 *
 * Catatan multi-tenant: sampai sync engine (S14) dibangun, tenant_id
 * dan owner_id sementara menggunakan user ID sebagai pseudo-tenant.
 *
 * Acuan: docs/02-UseCase.md UC-02, docs/03-Workflow.md WF-02
 * Requirement: FR-WS-01..04
 */
import { WorkspaceRepository, SyncMetaRepository, SYNC_META_KEYS } from './db';
import type { WorkspaceCache, UpdateWorkspaceInput } from './db';

// ---------------------------------------------------------------------------
// Repository singletons (semua berbagi koneksi DB singleton dari S5)
// ---------------------------------------------------------------------------
const workspaceRepo = new WorkspaceRepository();
const syncMetaRepo = new SyncMetaRepository();

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Nama workspace default yang dibuat otomatis (FR-WS-01). */
const DEFAULT_WORKSPACE_NAME = 'Workspace Pribadi';

// ---------------------------------------------------------------------------
// Reactive state (module-level, Svelte 5 runes)
// ---------------------------------------------------------------------------
let _workspaces = $state<WorkspaceCache[]>([]);
let _activeWorkspace = $state<WorkspaceCache | null>(null);
let _loading = $state(false);
let _error = $state<string | null>(null);

/** User ID yang sedang memiliki state ini. Null = belum di-init. */
let _initializedUserId: string | null = null;

// ---------------------------------------------------------------------------
// Init & Restore
// ---------------------------------------------------------------------------

/**
 * Inisialisasi workspace store untuk user tertentu.
 *
 * Alur:
 * 1. Skip jika sudah di-init untuk user yang sama (idempotent).
 * 2. Muat semua workspace milik user dari SQLite cache.
 * 3. Jika kosong → auto-create workspace default (FR-WS-01).
 * 4. Pulihkan workspace aktif dari sync_meta; fallback ke workspace pertama.
 *
 * Prasyarat: database SQLite sudah ter-init (initDatabase) dan user sudah
 * terautentikasi. Pemanggil bertanggung jawab mengatur urutan ini.
 *
 * @param userId ID user yang sedang login (juga dipakai sebagai pseudo-tenant_id).
 */
export async function initWorkspaces(userId: string): Promise<void> {
  // Idempotent — skip jika sudah di-init untuk user yang sama
  if (_initializedUserId === userId) return;
  _initializedUserId = userId;

  _loading = true;
  _error = null;

  try {
    // Muat workspace milik user (owner_id = userId, pseudo-tenant_id = userId)
    let workspaces = await workspaceRepo.findByOwner(userId);

    // FR-WS-01: Auto-create default workspace jika belum punya sama sekali
    if (workspaces.length === 0) {
      const defaultWorkspace = await workspaceRepo.create({
        tenant_id: userId,
        owner_id: userId,
        name: DEFAULT_WORKSPACE_NAME,
        description: null,
      });
      workspaces = [defaultWorkspace];
    }

    _workspaces = workspaces;

    // Pulihkan workspace aktif dari sync_meta (FR-WS-02: persistensi pilihan)
    const savedActiveId = await syncMetaRepo.get(SYNC_META_KEYS.activeWorkspaceId);
    let active: WorkspaceCache | null = savedActiveId
      ? (workspaces.find((w) => w.id === savedActiveId) ?? null)
      : null;

    // Fallback: jika tidak ada yang tersimpan atau ID tidak valid, pakai pertama
    if (!active && workspaces.length > 0) {
      active = workspaces[0];
      await syncMetaRepo.set(SYNC_META_KEYS.activeWorkspaceId, active.id);
    }

    _activeWorkspace = active;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat workspace';
    console.error('[workspace] Init failed:', err);
  } finally {
    _loading = false;
  }
}

// ---------------------------------------------------------------------------
// Switch Active Workspace
// ---------------------------------------------------------------------------

/**
 * Ganti workspace aktif (FR-WS-02).
 *
 * Memperbarui state reaktif dan mempersist pilihan ke sync_meta
 * agar tetap terpilih pada sesi berikutnya.
 *
 * @param id ID workspace yang akan diaktifkan.
 */
export async function switchWorkspace(id: string): Promise<void> {
  const target = _workspaces.find((w) => w.id === id);
  if (!target) {
    _error = `Workspace tidak ditemukan: ${id}`;
    console.warn('[workspace] switchWorkspace: ID not found:', id);
    return;
  }

  _activeWorkspace = target;
  await syncMetaRepo.set(SYNC_META_KEYS.activeWorkspaceId, id);
}

// ---------------------------------------------------------------------------
// CRUD Operations
// ---------------------------------------------------------------------------

/**
 * Buat workspace baru (FR-WS-03).
 *
 * Workspace baru otomatis menjadi bagian dari daftar. Tidak otomatis
 * di-switch — pemanggil bisa memanggil switchWorkspace() setelahnya.
 *
 * @returns Workspace yang dibuat, atau null jika gagal.
 */
export async function createWorkspace(
  name: string,
  description: string | null = null,
): Promise<WorkspaceCache | null> {
  const userId = _initializedUserId;
  if (!userId) {
    _error = 'Belum login — tidak dapat membuat workspace';
    return null;
  }

  const trimmedName = name.trim();
  if (!trimmedName) {
    _error = 'Nama workspace tidak boleh kosong';
    return null;
  }

  try {
    const created = await workspaceRepo.create({
      tenant_id: userId,
      owner_id: userId,
      name: trimmedName,
      description,
    });
    _workspaces = [..._workspaces, created];
    return created;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal membuat workspace';
    console.error('[workspace] Create failed:', err);
    return null;
  }
}

/**
 * Perbarui workspace yang ada (FR-WS-03).
 *
 * Jika workspace yang diubah adalah workspace aktif, state aktif
 * juga diperbarui agar UI langsung merefleksikan perubahan.
 *
 * @returns Workspace yang diperbarui, atau null jika gagal.
 */
export async function updateWorkspace(
  id: string,
  changes: UpdateWorkspaceInput,
): Promise<WorkspaceCache | null> {
  try {
    const updated = await workspaceRepo.update(id, changes);
    if (updated) {
      _workspaces = _workspaces.map((w) => (w.id === id ? updated : w));
      // Sinkronkan state aktif jika workspace yang diubah sedang aktif
      if (_activeWorkspace?.id === id) {
        _activeWorkspace = updated;
      }
    }
    return updated;
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memperbarui workspace';
    console.error('[workspace] Update failed:', err);
    return null;
  }
}

/**
 * Hapus workspace (FR-WS-04).
 *
 * Mencegah penghapusan workspace terakhir — user harus selalu punya
 * minimal satu workspace. Jika workspace yang dihapus adalah yang aktif,
 * otomatis beralih ke workspace pertama yang tersisa.
 */
export async function deleteWorkspace(id: string): Promise<void> {
  // Lindungi: tidak boleh hapus workspace terakhir
  if (_workspaces.length <= 1) {
    _error = 'Tidak dapat menghapus workspace terakhir';
    return;
  }

  try {
    await workspaceRepo.delete(id);
    _workspaces = _workspaces.filter((w) => w.id !== id);

    // Jika yang dihapus adalah workspace aktif, beralih ke pertama
    if (_activeWorkspace?.id === id) {
      const next = _workspaces[0];
      _activeWorkspace = next ?? null;
      if (next) {
        await syncMetaRepo.set(SYNC_META_KEYS.activeWorkspaceId, next.id);
      }
    }
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal menghapus workspace';
    console.error('[workspace] Delete failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Refresh & Reset
// ---------------------------------------------------------------------------

/**
 * Muat ulang daftar workspace dari cache (FR-WS-02).
 *
 * Dipakai setelah operasi eksternal (mis. sync engine S14) atau untuk
 * memastikan state UI sinkron dengan data di SQLite.
 */
export async function refreshWorkspaces(): Promise<void> {
  const userId = _initializedUserId;
  if (!userId) return;

  try {
    _workspaces = await workspaceRepo.findByOwner(userId);

    // Refresh data workspace aktif juga
    if (_activeWorkspace) {
      const refreshed = _workspaces.find((w) => w.id === _activeWorkspace!.id);
      if (refreshed) {
        _activeWorkspace = refreshed;
      } else {
        // Workspace aktif hilang (dihapus dari luar) → fallback ke pertama
        _activeWorkspace = _workspaces[0] ?? null;
      }
    }
  } catch (err) {
    _error = err instanceof Error ? err.message : 'Gagal memuat ulang workspace';
    console.error('[workspace] Refresh failed:', err);
  }
}

/**
 * Reset state workspace (dipanggil saat logout).
 *
 * Membersihkan semua state reaktif agar tidak ada data user sebelumnya
 * yang tertinggal di memori.
 */
export function resetWorkspaces(): void {
  _workspaces = [];
  _activeWorkspace = null;
  _loading = false;
  _error = null;
  _initializedUserId = null;
}

/** Hapus pesan error saat ini. */
export function clearWorkspaceError(): void {
  _error = null;
}

// ---------------------------------------------------------------------------
// Reactive Accessors
// ---------------------------------------------------------------------------

/**
 * Akses reaktif ke state workspace.
 * Pakai getter agar reaktivitas $state terjaga di konsumen.
 */
export const workspace = {
  /** Daftar semua workspace milik user. */
  get workspaces(): WorkspaceCache[] {
    return _workspaces;
  },
  /** Workspace yang sedang aktif, atau null jika belum ada. */
  get activeWorkspace(): WorkspaceCache | null {
    return _activeWorkspace;
  },
  /** ID workspace aktif (shortcut), atau null. */
  get activeWorkspaceId(): string | null {
    return _activeWorkspace?.id ?? null;
  },
  /** Nama workspace aktif (untuk tampilan topbar), atau null. */
  get activeWorkspaceName(): string | null {
    return _activeWorkspace?.name ?? null;
  },
  /** True jika sedang memuat data workspace. */
  get loading(): boolean {
    return _loading;
  },
  /** Pesan error terakhir, atau null jika tidak ada. */
  get error(): string | null {
    return _error;
  },
  /** Jumlah workspace yang dimiliki user. */
  get count(): number {
    return _workspaces.length;
  },
  /** True jika store sudah di-init untuk suatu user. */
  get isInitialized(): boolean {
    return _initializedUserId !== null;
  },
};
